import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { getMsal, isMsalAvailable } from '@/lib/msal'

export function OutlookReturn() {
  const navigate = useNavigate()
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    async function run() {
      if (!isMsalAvailable()) {
        navigate('/ai', { replace: true })
        return
      }
      const msal = getMsal()
      if (!msal) {
        setError('MSAL is not initialized')
        return
      }
      try {
        await msal.initialize()
        // Snapshot what Microsoft actually sent back BEFORE
        // handleRedirectPromise consumes the URL — handles the silent
        // failure mode where Azure's redirect URI is registered as "Web"
        // instead of "SPA" (MSAL drops the code without throwing).
        const incomingHash = window.location.hash || ''
        const incomingSearch = window.location.search || ''
        const sp = new URLSearchParams(incomingSearch.startsWith('?') ? incomingSearch.slice(1) : incomingSearch)
        const hp = new URLSearchParams(incomingHash.startsWith('#') ? incomingHash.slice(1) : incomingHash)
        const msError = sp.get('error') || hp.get('error')
        const msErrorDesc = sp.get('error_description') || hp.get('error_description')
        const hasCode = !!(sp.get('code') || hp.get('code'))

        // Microsoft sent an explicit error — surface it. Common: consent
        // declined, conditional-access policy, admin-consent required.
        if (msError) {
          setError(`${msError} · ${msErrorDesc ?? 'no description'}`)
          return
        }

        const result = await msal.handleRedirectPromise()
        if (result?.account) {
          // Notify the backend (the outlook Lambda's /callback expects ?code= but we use MSAL.js for token acquisition,
          // so we just inform the backend of the account email; refresh-token storage happens server-side later if needed)
          // Then bounce back to /ai
          navigate('/ai?outlook=connected', { replace: true })
          return
        }

        // result is null but MS DID send a code → MSAL couldn't redeem it.
        // 99% of the time this is the Azure-app "Web" vs "Single-page
        // application" platform mismatch. Tell the operator exactly what
        // to fix instead of silently bouncing back to /ai.
        if (hasCode) {
          setError(
            "Microsoft returned an auth code but MSAL couldn't redeem it. " +
            "Verify the Azure App Registration → Authentication → has the " +
            "redirect URI registered under 'Single-page application', not 'Web'.",
          )
          return
        }

        // Truly nothing to process — direct visit to /outlook/return.
        navigate('/ai', { replace: true })
      } catch (err) {
        // MSAL errors carry the AAD error code (e.g. AADSTS9002326) on
        // .errorCode + .errorMessage; plain Error has only .message. Show
        // both so the operator can paste the AAD code to support without
        // a console dive. Also log the raw error to the console for
        // anything we don't unpack here.
        console.error('[outlook-return] handleRedirectPromise failed', err)
        const e = err as { errorCode?: string; errorMessage?: string; message?: string }
        const parts = [e.errorCode, e.errorMessage ?? e.message].filter(Boolean)
        setError(parts.join(' · ') || 'Outlook sign-in failed')
      }
    }
    void run()
  }, [navigate])

  return (
    <div className="grid h-screen supports-[height:100dvh]:h-dvh place-items-center bg-slate-50 p-6">
      <div className="penser-card p-6 text-center">
        {error ? (
          <>
            <div className="mb-2 text-sm font-semibold text-red-700">Outlook sign-in failed</div>
            <div className="text-xs text-slate-500">{error}</div>
            <button className="penser-button-ghost mt-3" onClick={() => navigate('/ai')}>
              Back to AI assistant
            </button>
          </>
        ) : (
          <>
            <div className="penser-thinking mx-auto mb-2">
              <img src="/penser-icon.png" alt="" width={36} />
            </div>
            <div className="text-sm text-slate-600">Completing Outlook sign-in…</div>
          </>
        )}
      </div>
    </div>
  )
}
